// 001 — Admisión-Alta-Egreso (Anexo 1 MSP, SNS-MSP/HCU-form. 001/2021): lo que la página `Formulario001Page.tsx` no traía del papel
// (páginas 4-5 del PDF; spec en docs/hce-anexos/001-admision-alta-egreso.md). Los bloques A, C y D (establecimiento, filiación y
// contacto/representante) ya se capturan en el encabezado MSP y en Datos Demográficos; aquí están B (admisionista), E (registro
// de cambios, 3 bloques de 14 campos) y F (egreso hospitalario, 6 órdenes).
//
// Equivalencia funcional, no facsímil. Decisiones:
// - E: los 3 registros de cambios son texto libre, como en el papel (estado civil y seguro no son casillas allí). Solo van al PDF
//   firmado: los datos vigentes del paciente se editan en Datos Demográficos.
// - A (institución, unicódigo, establecimiento, archivo y datos del paciente) lo captura `EncabezadoMspCampos` en la página y sale
//   en el encabezado MSP del PDF firmado. C y D viven en Datos Demográficos (ver la lista de brechas en el PR).
// - Presuntivo/Definitivo: el papel pone PRESUNTIVO junto al principal y DEFINITIVO junto al secundario sin aclarar si es una
//   regla; se ofrecen las dos marcas en cada diagnóstico (superconjunto).
// - F: 6 órdenes con todas las columnas del papel. Cada orden tiene 1 diagnóstico principal y 1 secundario con CIE y
//   Presuntivo/Definitivo (el papel da 2 renglones por orden sin aclarar si son 2 diagnósticos o un renglón que se desborda).
//   "Fechas de admisión y egreso" son dos fechas (la línea superior es la admisión y la inferior el egreso; el papel no lo rotula).
//   El código de servicio es el catálogo de 50 de la misma hoja. Firma y sello del responsable no son campos.
// - FHIR: los diagnósticos de egreso crean una `Condition` cada uno y se enlazan al `Encounter.diagnosis` (rol DD) en la MISMA
//   transacción que el PUT del Encounter. Solo se crean si el Encounter aún no tiene diagnósticos de egreso (guardar de nuevo no
//   duplica). Todo lo demás (E y el resto de F) va como `Par[]` en el PDF firmado.
import type { Condition, Encounter, EncounterDiagnosis, Patient, Reference } from '@medplum/fhirtypes';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca, type DiagnosticoConMarca, type OpcionMarca } from './MarcaDiagnostico';
import { paresDeCampos, valoresVacios, type CampoSimple, type Valores } from './camposSimplesDatos';
import type { Par } from './index';

export const NUM_CAMBIOS_001 = 3;
export const NUM_ORDENES_001 = 6;
export const MARCAS_001: readonly OpcionMarca[] = [
  { valor: 'presuntivo', texto: 'Presuntivo' },
  { valor: 'definitivo', texto: 'Definitivo' },
];

const texto = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta });

// ---------- B. Registro de admisión ----------

export const CAMPOS_ADMISION_001: readonly CampoSimple[] = [texto('admisionista', 'Nombre y apellido del admisionista')];

// ---------- E. Registro de cambios (3 bloques idénticos) ----------

export const CAMPOS_CAMBIO_001: readonly CampoSimple[] = [
  { clave: 'fecha', etiqueta: 'Fecha de registro', tipo: 'fecha' },
  texto('estadoCivil', 'Estado civil'),
  texto('nivelEducacion', 'Nivel de educación'),
  texto('tipoEmpresa', 'Tipo empresa trabajo'),
  texto('ocupacion', 'Ocupación / profesión principal'),
  texto('seguro', 'Seguro de salud principal'),
  texto('provincia', 'Residencia habitual — Provincia'),
  texto('canton', 'Residencia habitual — Cantón'),
  texto('parroquia', 'Residencia habitual — Parroquia'),
  texto('barrio', 'Residencia habitual — Barrio o sector'),
  texto('telefono', 'Residencia habitual — No. teléfono'),
  texto('callePrincipal', 'Residencia habitual — Calle principal'),
  texto('calleSecundaria', 'Residencia habitual — Calle secundaria'),
  texto('referencia', 'Residencia habitual — Referencia'),
];

// ---------- F. Egreso hospitalario (6 órdenes) ----------

/** Catálogo "Código del servicio" de la hoja EGRESO (1-50). */
export const SERVICIOS_001: readonly string[] = [
  'Alergología', 'Atención Primaria de la Salud', 'Cardiología', 'Cardiopediatría', 'Cirugía Cardiaca', 'Cirugía Toráxica', 'Cirugía General', 'Cirugía Máxilo Facial',
  'Cirugía Oncológica', 'Cirugía Pediátrica', 'Cirugía Plástica', 'Cirugía Vascular', 'Clínica del Dolor', 'Dermatología', 'Endocrinología', 'Endodoncia',
  'Gastroenterología', 'Genética', 'Geriatría', 'Gerontología', 'Ginecología', 'Hematología', 'Infectología', 'Medicina Alternativa',
  'Medicina Familiar', 'Medicina Física y Rehabilitación', 'Medicina Interna', 'Nefrología', 'Neonatología', 'Neumología', 'Neurología Clínica', 'Neuro Psicología',
  'Neurocirugía', 'Neurología', 'Odonto Pediatría', 'Oftalmología', 'Oncología', 'Onco Hematología', 'Ortodoncia', 'Otorrinolaringología',
  'Pediatría', 'Periodoncia', 'Proctología', 'Psiquiatría', 'Rehabilitación Oral', 'Reumatología', 'Traumatología', 'Urología',
  'Obstetricia', 'Otra',
];

export const CAMPOS_ORDEN_001: readonly CampoSimple[] = [
  { clave: 'fechaAdmision', etiqueta: 'Fecha de admisión', tipo: 'fecha' },
  { clave: 'fechaEgreso', etiqueta: 'Fecha de egreso', tipo: 'fecha' },
  { clave: 'diasEstada', etiqueta: 'Número de días de estada', tipo: 'numero' },
  {
    clave: 'servicio',
    etiqueta: 'Código del servicio',
    tipo: 'select',
    opciones: SERVICIOS_001.map((s, i) => ({ valor: String(i + 1), etiqueta: `${i + 1}. ${s}` })),
  },
  {
    clave: 'condicionEgreso',
    etiqueta: 'Condición del egreso',
    tipo: 'select',
    opciones: [
      { valor: 'alta', etiqueta: 'Alta' },
      { valor: 'muerte-menos-48', etiqueta: 'Muerte menos de 48 horas' },
      { valor: 'muerte-mas-48', etiqueta: 'Muerte más de 48 horas' },
    ],
  },
  { clave: 'clinico', etiqueta: 'Tratamiento clínico', tipo: 'casilla' },
  { clave: 'quirurgico', etiqueta: 'Tratamiento quirúrgico', tipo: 'casilla' },
  { clave: 'procedimientos', etiqueta: 'Procedimientos clínicos o quirúrgicos principales', tipo: 'area' },
];

export interface Orden001 {
  readonly valores: Valores;
  readonly principal: DiagnosticoConMarca;
  readonly secundario: DiagnosticoConMarca;
}

// ---------- Estado ----------

export interface Estado001 {
  readonly admision: Valores;
  readonly cambios: readonly Valores[];
  readonly ordenes: readonly Orden001[];
}

export function estadoInicial001(): Estado001 {
  return {
    admision: valoresVacios(CAMPOS_ADMISION_001),
    cambios: Array.from({ length: NUM_CAMBIOS_001 }, () => valoresVacios(CAMPOS_CAMBIO_001)),
    ordenes: Array.from({ length: NUM_ORDENES_001 }, () => ({ valores: valoresVacios(CAMPOS_ORDEN_001), principal: diagnosticoConMarcaVacio(), secundario: diagnosticoConMarcaVacio() })),
  };
}

// ---------- FHIR ----------

export interface DiagnosticoEgreso001 {
  readonly orden: number;
  readonly rol: 'principal' | 'secundario';
  readonly diagnostico: DiagnosticoConMarca;
}

/** Diagnósticos de egreso con texto, en el orden del papel (por orden: principal y luego secundario). */
export function diagnosticosDeEgreso001(e: Estado001): DiagnosticoEgreso001[] {
  return e.ordenes.flatMap((o, i) =>
    (
      [
        ['principal', o.principal],
        ['secundario', o.secundario],
      ] as const
    )
      .filter(([, d]) => d.diagnostico.texto.trim() !== '')
      .map(([rol, diagnostico]) => ({ orden: i + 1, rol, diagnostico })),
  );
}

const SISTEMA_ROL_DIAGNOSTICO = 'http://terminology.hl7.org/CodeSystem/diagnosis-role';

/** ¿El Encounter ya tiene diagnósticos de egreso (rol DD)? Entonces no se vuelven a crear. */
export function yaTieneDiagnosticosDeEgreso(encounter: Encounter): boolean {
  return (encounter.diagnosis ?? []).some((d) => d.use?.coding?.some((c) => c.system === SISTEMA_ROL_DIAGNOSTICO && c.code === 'DD'));
}

/** `Condition` de un diagnóstico de egreso. */
export function conditionDeEgreso001(d: DiagnosticoEgreso001, subject: Reference<Patient>, encounter: Reference<Encounter>, ahoraIso: string): Condition {
  return { resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(d.diagnostico.diagnostico), recordedDate: ahoraIso };
}

/** Entrada de `Encounter.diagnosis` (rol DD) que apunta a la Condition por su `urn:uuid`; `rank` sigue el orden del papel. */
export function diagnosisDeEncounter001(referencia: string, rank: number): EncounterDiagnosis {
  return { condition: { reference: referencia }, use: { coding: [{ system: SISTEMA_ROL_DIAGNOSTICO, code: 'DD', display: 'Discharge diagnosis' }] }, rank };
}

// ---------- PDF ----------

/** Serializa B, E y F a `Par[]` (filtra vacíos). Los campos del Encounter ya salen en `Formulario001Page`. */
export function paresDe001(e: Estado001): Par[] {
  const pares: Par[] = [...paresDeCampos(CAMPOS_ADMISION_001, e.admision, 'B. Registro de admisión — ')];
  e.cambios.forEach((c, i) => pares.push(...paresDeCampos(CAMPOS_CAMBIO_001, c, `E. Registro de cambios ${i + 1} — `)));
  e.ordenes.forEach((o, i) => {
    const prefijo = `F. Egreso hospitalario, orden ${i + 1} — `;
    pares.push(...paresDeCampos(CAMPOS_ORDEN_001, o.valores, prefijo));
    for (const [rol, d] of [
      ['Diagnóstico o síndrome (principal)', o.principal],
      ['Diagnóstico o síndrome (secundario)', o.secundario],
    ] as const) {
      pares.push({ pregunta: `${prefijo}${rol}`, respuesta: textoDiagnosticoConMarca(d, MARCAS_001) });
    }
  });
  return pares.filter((p) => p.respuesta.trim() !== '');
}
